import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  listSupportTickets,
  updateSupportTicket,
  type SupportTicket,
  type SupportTicketStatus,
} from '@/lib/api/admin'

export function AdminSupport() {
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [banner, setBanner] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<SupportTicketStatus | 'all'>('all')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listSupportTickets({ limit: 100 })
      .then(({ items }) => {
        if (cancelled) return
        setTickets(items)
        if (items.length > 0 && !selectedId) setSelectedId(items[0].id)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load tickets')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (tickets.length > 0 && !selectedId) setSelectedId(tickets[0].id)
  }, [tickets, selectedId])

  const filteredTickets =
    statusFilter === 'all'
      ? tickets
      : tickets.filter((t) => t.status === statusFilter)

  const selected = selectedId
    ? tickets.find((t) => t.id === selectedId || t.ticketId === selectedId)
    : null

  const handleUpdateStatus = async (ticketId: string, status: SupportTicketStatus) => {
    try {
      await updateSupportTicket(ticketId, { status })
      setTickets((current) =>
        current.map((t) => (t.ticketId === ticketId || t.id === ticketId ? { ...t, status } : t)),
      )
      setBanner(`Ticket marked as ${status}.`)
      setTimeout(() => setBanner(null), 4000)
    } catch (err) {
      setBanner(err instanceof Error ? err.message : 'Update failed')
      setTimeout(() => setBanner(null), 4000)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Support inbox</h1>
          <p className="mt-1 text-sm text-gray-400">
            View and manage support requests from viewers and creators. Status updates are saved to
            the backend.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/30 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}

      {banner && (
        <div className="rounded-lg border border-emerald-700 bg-emerald-900/30 px-3 py-2 text-xs text-emerald-200">
          {banner}
        </div>
      )}

      <div className="inline-flex rounded-full bg-white/5 p-1 text-xs">
        {(['all', 'open', 'pending', 'closed'] as const).map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1 rounded-full capitalize ${
              statusFilter === status ? 'bg-[#D4AF37] text-black' : 'text-gray-300 hover:bg-white/5'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,2fr)]">
        <div className="rounded-xl border border-gray-800 bg-black/40 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-800 text-xs font-medium uppercase tracking-wide text-gray-500">
            Tickets
          </div>
          {loading ? (
            <div className="px-3 py-6 text-sm text-gray-400">Loading tickets…</div>
          ) : filteredTickets.length === 0 ? (
            <div className="px-3 py-6 text-sm text-gray-400">
              No tickets yet. When viewers or creators submit support requests, they will appear
              here.
            </div>
          ) : (
            <div className="divide-y divide-gray-900 max-h-[360px] overflow-y-auto">
              {filteredTickets.map((ticket) => (
                <button
                  key={ticket.ticketId}
                  type="button"
                  onClick={() => setSelectedId(ticket.ticketId)}
                  className={`w-full px-3 py-3 text-left text-sm transition-colors ${
                    selected?.ticketId === ticket.ticketId ? 'bg-white/5' : 'hover:bg-white/5'
                  }`}
                >
                  <p className="font-medium text-white line-clamp-1">{ticket.subject}</p>
                  <p className="text-xs text-gray-400">{ticket.email || 'No email'}</p>
                  <div className="mt-1 flex items-center justify-between text-[11px] text-gray-500">
                    <span>
                      {ticket.createdAt
                        ? new Date(ticket.createdAt).toLocaleDateString()
                        : '—'}
                    </span>
                    <span className="capitalize">{ticket.status}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-800 bg-black/40 p-4 flex flex-col gap-3 min-h-[260px]">
          {selected ? (
            <>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Subject</p>
                <p className="text-sm font-medium text-white">{selected.subject}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs text-gray-400">
                <div>
                  <p className="uppercase tracking-wide text-[11px] text-gray-500 mb-0.5">From</p>
                  <p>{selected.email || '—'}</p>
                </div>
                <div>
                  <p className="uppercase tracking-wide text-[11px] text-gray-500 mb-0.5">Created</p>
                  <p>
                    {selected.createdAt
                      ? new Date(selected.createdAt).toLocaleString()
                      : '—'}
                  </p>
                </div>
              </div>
              {selected.userId && (
                <div className="text-xs">
                  <Link
                    to={`/admin/users/${selected.userId}`}
                    className="text-[#D4AF37] hover:underline"
                  >
                    View user profile →
                  </Link>
                </div>
              )}
              {selected.body && (
                <div>
                  <p className="uppercase tracking-wide text-[11px] text-gray-500 mb-1">Message</p>
                  <p className="text-sm text-gray-300 whitespace-pre-wrap">{selected.body}</p>
                </div>
              )}
              <div className="mt-1">
                <p className="uppercase tracking-wide text-[11px] text-gray-500 mb-1">Reply</p>
                <textarea
                  className="w-full rounded-lg border border-gray-800 bg-black/40 px-2 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                  rows={4}
                  placeholder="Add internal notes or prepare a reply (sending email not implemented)."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  size="xs"
                  className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black"
                  onClick={() => {
                    handleUpdateStatus(selected.ticketId, 'pending')
                    setReplyText('')
                  }}
                >
                  Mark as pending
                </Button>
                <Button
                  size="xs"
                  variant="secondary"
                  className="border-emerald-700 text-emerald-300 hover:bg-emerald-900/20"
                  onClick={() => handleUpdateStatus(selected.ticketId, 'closed')}
                >
                  Mark as closed
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-400">Select a ticket from the left to view details.</p>
          )}
        </div>
      </div>
    </div>
  )
}
